import { Alert, AlertTitle, Box, Button, TextField } from "@mui/material";
import { FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogShell } from "../general/DialogShell";
import { useAuth } from "./ParseAuthContext";

/** The fewest characters of a code: those of one from the authenticator app. */
const CODE_LENGTH = 6;

export const MfaResetDialog = ({
  open,
  onClose,
  onDisabled,
}: {
  open: boolean;
  onClose: () => void;
  onDisabled: () => void;
}) => {
  const { disableMfa } = useAuth();
  const { t } = useTranslation(undefined, { keyPrefix: "mfa.reset" });
  const formId = useId();
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    if (submitting || token.length < CODE_LENGTH) return;
    setSubmitting(true);
    setFailed(false);
    try {
      await disableMfa(token);
      setToken("");
      onDisabled();
    } catch (err) {
      console.warn("Failed to reset MFA", err);
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    if (submitting) return;
    setToken("");
    setFailed(false);
    onClose();
  }

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      title={t("title")}
      description={t("description")}
      actions={
        <>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="contained"
            color="error"
            disabled={token.length < CODE_LENGTH}
            loading={submitting}
          >
            {t("confirm")}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id={formId}
        onSubmit={handleConfirm}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={t("tokenLabel")}
          value={token}
          onChange={({ target: { value } }) =>
            setToken(value.replace(/\s+/g, ""))
          }
          slotProps={{ htmlInput: { autoComplete: "one-time-code" } }}
          disabled={submitting}
        />
        {failed && (
          <Alert severity="error">
            <AlertTitle>{t("errorTitle")}</AlertTitle>
            {t("error")}
          </Alert>
        )}
        <Alert severity="warning">{t("warning")}</Alert>
      </Box>
    </DialogShell>
  );
};
